import { CircleAlert, CircleCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { PackagingItemComplianceRedFlag } from '@/shared/hooks/api/packagingItemsOverview'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card'
import {
  redFlagLabelKey,
} from './packagingSupplierOverviewColumns'

type PackagingItemComplianceRedFlagsCellProps = {
  complianceRedFlags: PackagingItemComplianceRedFlag[]
}

export function PackagingItemComplianceRedFlagsCell({
  complianceRedFlags,
}: PackagingItemComplianceRedFlagsCellProps) {
  const { t } = useTranslation('packagingSuppliers')

  if (complianceRedFlags.length === 0) {
    const ariaLabel = t('list.complianceRedFlags.empty')
    return (
      <HoverCard>
        <HoverCardTrigger asChild>
          <button
            type='button'
            className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
            aria-label={ariaLabel}
            onClick={(event) => event.stopPropagation()}
          >
            <CircleCheck
              className='size-4 text-green-600 dark:text-green-500'
              aria-hidden
            />
          </button>
        </HoverCardTrigger>
        <HoverCardContent className='w-80 max-w-sm p-4' align='start' side='top'>
          <p className='text-sm text-popover-foreground'>{ariaLabel}</p>
        </HoverCardContent>
      </HoverCard>
    )
  }

  const summaryLabel = t('list.complianceRedFlags.hoverTitle')

  return (
    <HoverCard>
      <HoverCardTrigger asChild>
        <button
          type='button'
          className='inline-flex rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
          aria-label={summaryLabel}
          onClick={(event) => event.stopPropagation()}
        >
          <CircleAlert className='size-4 text-destructive' aria-hidden />
        </button>
      </HoverCardTrigger>
      <HoverCardContent className='w-80 max-w-sm p-4' align='start' side='top'>
        <ul className='space-y-3'>
          {complianceRedFlags.map((flag) => (
            <li key={flag} className='flex items-start gap-2'>
              <CircleAlert
                className='mt-0.5 size-4 shrink-0 text-destructive'
                aria-hidden
              />
              <div className='min-w-0 space-y-0.5'>
                <p className='text-sm font-medium text-popover-foreground'>
                  {t(redFlagLabelKey(flag))}
                </p>
                <p className='text-sm text-muted-foreground'>
                  {t(`list.complianceRedFlags.issues.${flag}.description`)}
                </p>
              </div>
            </li>
          ))}
        </ul>
      </HoverCardContent>
    </HoverCard>
  )
}
